import { useState } from 'react';
import { generateSecretKey, finalizeEvent, nip57 } from 'nostr-tools';
import { Copy, Loader2, Zap } from 'lucide-react';

import { QRCodeCanvas } from '@/components/ui/qrcode';
import { Button } from '@/components/ui/button';
import { useAppContext } from '@/hooks/useAppContext';
import { useAuthor } from '@/hooks/useAuthor';
import { useEventById } from '@/hooks/useAddressableEvent';
import { useVerifyWebAuthor } from '@/hooks/useVerifyWebAuthor';
import { useWallet } from '@/hooks/useWallet';
import { fetchJsonResilient, getLnurlPayInfo, zapInvoiceUrl } from '@/lib/lnurl';
import { primarySource, type Highlight } from '@/lib/highlights';
import { cn } from '@/lib/utils';

const PRESET_AMOUNTS = [21, 100, 500];

type ZapState = 'idle' | 'loading' | 'invoice' | 'zapped' | 'error';

interface EmbedZapButtonProps {
  highlight: Highlight;
  /** Total sats already zapped (for display). */
  totalSats?: number;
  /** Permalink to fall back to / link out to the full app. */
  permalink: string;
}

function hasLightning(metadata: { lud16?: string; lud06?: string } | undefined): boolean {
  return !!(metadata?.lud16 || metadata?.lud06);
}

/**
 * Zap button for embedded highlights.
 *
 * Embeds serve the open web, not glowmark users — so zap requests are signed
 * with an ephemeral keypair (anonymous zaps) and paid via WebLN when present,
 * otherwise via QR / lightning: link.
 *
 * Author-first: when the highlight credits an original author (p tag) who has
 * a lightning address, the payment goes to them — the words earn, not just
 * the marking. Weighted `p` tags declare a 75/25 author/highlighter split for
 * split-aware LNURL servers (others simply pay the primary recipient).
 */
export function EmbedZapButton({ highlight, totalSats, permalink }: EmbedZapButtonProps) {
  const { event, sourceAuthors } = highlight;
  const highlighterPubkey = event.pubkey;
  const source = primarySource(highlight.sources);
  const claimedAuthorPubkey = sourceAuthors[0];

  // Author attribution must be *provable*, not just claimed in a p tag:
  // - article source → the coordinate's pubkey (the article is signed by them)
  // - note source → the referenced note's pubkey (fetched; also signed)
  // - web source → the claimed author, only after independent verification
  //   (rel=me on the page, or a NIP-05 domain handshake)
  const noteSource = source?.type === 'note' ? source : null;
  const noteEvent = useEventById(noteSource?.id ?? '');

  const needsVerification = source?.type === 'url' && !!claimedAuthorPubkey;
  const verification = useVerifyWebAuthor(
    source?.type === 'url' ? source.url : undefined,
    needsVerification ? claimedAuthorPubkey : undefined,
  );
  const webAuthorVerified = verification.data === 'rel-me' || verification.data === 'domain';
  const webClaimRejected = needsVerification && verification.data === 'unverified';

  const credibleAuthorPubkey =
    source?.type === 'article'
      ? source.pubkey
      : source?.type === 'note'
        ? noteEvent.data?.pubkey
        : webAuthorVerified
          ? claimedAuthorPubkey
          : undefined;

  const splitsWithHighlighter = !!credibleAuthorPubkey && credibleAuthorPubkey !== highlighterPubkey;

  const credibleAuthor = useAuthor(credibleAuthorPubkey);
  const highlighter = useAuthor(highlighterPubkey);
  const { webln } = useWallet();
  const { config } = useAppContext();

  const [state, setState] = useState<ZapState>('idle');
  const [invoice, setInvoice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Who gets paid: the provable original author when payable, else the highlighter.
  const recipient = hasLightning(credibleAuthor.data?.metadata) && credibleAuthorPubkey
    ? { pubkey: credibleAuthorPubkey, metadata: credibleAuthor.data?.metadata, isAuthor: true }
    : hasLightning(highlighter.data?.metadata)
      ? { pubkey: highlighterPubkey, metadata: highlighter.data?.metadata, isAuthor: false }
      : null;

  const zap = async (sats: number) => {
    setState('loading');
    setInvoice(null);

    try {
      if (!recipient?.metadata) throw new Error('No payable recipient');

      const payInfo = await getLnurlPayInfo(recipient.metadata);
      if (!payInfo?.allowsNostr) throw new Error('No zap endpoint');

      // nostr-tools v2.23 API: `pubkey` (not `profile`) and the full event
      // object (not an id string) — it derives e/a/k tags itself.
      const zapRequest = nip57.makeZapRequest({
        pubkey: recipient.pubkey,
        event,
        amount: sats * 1000,
        relays: config.relayMetadata.relays.map((r) => r.url),
        comment: 'zapped via a glowmark embed',
      });

      // Declare the author/highlighter split (NIP-57 zap splits). Servers
      // without split support pay the primary (first) recipient only.
      zapRequest.tags = zapRequest.tags.filter(([name]) => name !== 'p');
      if (splitsWithHighlighter && recipient.isAuthor) {
        zapRequest.tags.push(['p', recipient.pubkey, '', '3']);
        zapRequest.tags.push(['p', highlighterPubkey, '', '1']);
      } else {
        zapRequest.tags.push(['p', recipient.pubkey]);
      }

      // Anonymous zap: sign with a throwaway keypair.
      const signed = finalizeEvent(zapRequest, generateSecretKey());

      // LNURL servers are often CORS-hostile — fetch resiliently.
      const data = (await fetchJsonResilient(
        zapInvoiceUrl(payInfo.callback, sats * 1000, signed),
      )) as { pr?: unknown; reason?: string } | null;
      const pr = data?.pr;
      if (typeof pr !== 'string' || !pr) {
        throw new Error(data?.reason || 'Lightning service did not return an invoice');
      }

      // Fast path: pay directly with a browser wallet when available.
      if (webln) {
        try {
          if (typeof webln.enable === 'function') await webln.enable();
          await webln.sendPayment(pr);
          setState('zapped');
          return;
        } catch {
          // Fall through to the invoice/QR view.
        }
      }

      setInvoice(pr);
      setState('invoice');
    } catch (error) {
      console.warn('Embed zap failed:', error);
      setState('error');
    }
  };

  const copyInvoice = async () => {
    if (!invoice) return;
    try {
      await navigator.clipboard.writeText(invoice);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable in some embed contexts — the invoice is selectable anyway.
    }
  };

  // Where the author-invite link points: the page's glowmark view (which
  // carries the rel=me onboarding), else the highlight permalink.
  const urlSource = highlight.sources.find((s) => s.type === 'url');
  const inviteHref = urlSource
    ? `${window.location.origin}/web/${encodeURIComponent(urlSource.url)}`
    : permalink;

  const resolving =
    (noteSource !== null && noteEvent.isPending) ||
    (needsVerification && verification.isPending) ||
    (!!credibleAuthorPubkey && credibleAuthor.isPending) ||
    highlighter.isPending;

  // No provable, payable recipient → count + invite link, no button.
  if (!resolving && !recipient) {
    return (
      <span className="inline-flex items-center gap-1.5 flex-wrap text-xs text-muted-foreground">
        {totalSats ? (
          <span className="inline-flex items-center gap-1">
            <Zap className="size-3.5" />
            {totalSats.toLocaleString()}
          </span>
        ) : null}
        <a
          href={inviteHref}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 hover:text-foreground"
        >
          author can't receive zaps yet — invite them
        </a>
      </span>
    );
  }

  return (
    <div className="min-w-0">
      {state === 'idle' || state === 'loading' ? (
        <span className="inline-flex items-center gap-1 flex-wrap">
          {PRESET_AMOUNTS.map((sats) => (
            <button
              key={sats}
              type="button"
              onClick={() => zap(sats)}
              disabled={state === 'loading' || resolving}
              title={
                recipient?.isAuthor
                  ? 'Zap the verified original author (split-aware servers share with the highlighter)'
                  : 'Zap the highlighter'
              }
              className="inline-flex items-center gap-0.5 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <Zap className="size-3" />
              {sats}
            </button>
          ))}
          {(state === 'loading' || resolving) && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
          {resolving ? (
            <span className="text-[10px] text-muted-foreground/70">verifying author…</span>
          ) : recipient?.isAuthor ? (
            <span className="text-[10px] text-muted-foreground/70">
              {needsVerification ? 'pays the verified author' : 'pays the author'}
            </span>
          ) : webClaimRejected && recipient ? (
            <span className="text-[10px] text-muted-foreground/70">
              author claim unverified — pays the highlighter
            </span>
          ) : null}
        </span>
      ) : state === 'zapped' ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
          <Zap className="size-3.5 fill-current" />
          Zapped — thanks!
        </span>
      ) : state === 'error' ? (
        <span className="text-xs text-muted-foreground">
          Zap unavailable here —{' '}
          <a href={permalink} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground">
            zap on glowmark
          </a>
        </span>
      ) : null}

      {/* Invoice panel (QR + copy) */}
      {state === 'invoice' && invoice && (
        <div className="mt-2 rounded-lg border border-border bg-background p-3 space-y-2">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-md bg-white p-1">
              <QRCodeCanvas value={`lightning:${invoice}`} size={96} />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-xs text-muted-foreground leading-snug">
                Scan with any Lightning wallet to zap{recipient?.isAuthor ? ' the author' : ''}.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="secondary" className="h-7 text-xs" asChild>
                  <a href={`lightning:${invoice}`}>Open wallet</a>
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={copyInvoice}>
                  <Copy className="size-3" />
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setState('idle')}>
                  Back
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Existing zap total */}
      {totalSats ? (
        <span className={cn('mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground')}>
          <Zap className="size-3" />
          {totalSats.toLocaleString()} sats zapped
        </span>
      ) : null}
    </div>
  );
}
